/**
 * Timezone-aware local date helpers for the Planner.
 *
 * PlannerItem.date is a local wall-clock date (yyyy-mm-dd). All helpers here
 * use the device-local timezone — never UTC conversions that would shift a
 * day boundary (spec §8.3: "timezone-aware dates").
 */

/** Pads to two digits. */
function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}

/** Local date (yyyy-mm-dd) of a Date, in the device timezone. */
export function toLocalDateString(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

/** Today as a local yyyy-mm-dd string. */
export function localToday(): string {
  return toLocalDateString(new Date())
}

/** Adds whole days to a local yyyy-mm-dd date, DST-safe via local Date math. */
export function addDaysToLocalDate(date: string, days: number): string {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number)
  const result = new Date(year, month - 1, day)
  result.setDate(result.getDate() + days)
  return toLocalDateString(result)
}

/** The seven planner days: today plus the six following days. */
export function nextSevenLocalDates(): string[] {
  const today = localToday()
  return Array.from({ length: 7 }, (_, index) => addDaysToLocalDate(today, index))
}

/** Parses yyyy-mm-dd into a local Date (midnight local time). */
export function parseLocalDate(date: string): Date {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const shortDayFormatter = new Intl.DateTimeFormat('nl-NL', { weekday: 'short' })
const dayMonthFormatter = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' })
const longFormatter = new Intl.DateTimeFormat('nl-NL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

export type DayLabel = {
  /** Chip text, e.g. "ma 30 jul" or "Vandaag 30 jul". */
  short: string
  /** Full accessible label, e.g. "maandag 30 juli" / "Vandaag, maandag 30 juli". */
  long: string
  isToday: boolean
}

/** Exact Dutch labels for a day chip (nl-NL, device timezone). */
export function dayLabelFor(date: string): DayLabel {
  const parsed = parseLocalDate(date)
  const isToday = date === localToday()
  const weekday = shortDayFormatter.format(parsed)
  const dayMonth = dayMonthFormatter.format(parsed)
  const longDate = longFormatter.format(parsed)
  return {
    short: isToday ? `Vandaag ${dayMonth}` : `${weekday} ${dayMonth}`,
    long: isToday ? `Vandaag, ${longDate}` : longDate,
    isToday,
  }
}

/** Formats minutes-from-midnight as HH:mm. */
export function formatMinutes(minutes: number): string {
  const clamped = Math.min(Math.max(Math.round(minutes), 0), 24 * 60)
  const hours = Math.floor(clamped / 60)
  const mins = clamped % 60
  return `${pad2(hours)}:${pad2(mins)}`
}

/** Parses HH:mm into minutes-from-midnight; null when invalid. */
export function parseTimeToMinutes(time: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/** Dutch duration label, e.g. 90 → "1 uur 30 min". */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  const hourLabel = hours === 1 ? '1 uur' : `${hours} uur`
  return rest === 0 ? hourLabel : `${hourLabel} ${rest} min`
}
